import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setSidebarStatus } from "actions/user";
import Arrow from "assets/images/leftArr.png";
import { get } from "api/index";
import "../Dashboard/dashboard.scss";

/**
 * SEO Analytics page — sibling of Post Analytics, under SEO Management.
 * Per-brand engagement on the learning FAQ / Q&A: views, up/down votes,
 * real-user questions (asked by visitors, not Prigmar's auto-FAQ) + article
 * counts. Endpoints (scope-aware): /seo-analytics/{totals,brands,questions}.
 */

const BRAND_COLORS = ["#0F62FE", "#22c55e", "#ef4444", "#a855f7", "#f59e0b", "#06b6d4", "#ec4899", "#84cc16"];

function StatCard({ label, value, accent = "#0F62FE", sub }) {
  return (
    <div className="dashboard-cards card border-0">
      <div className="card-body">
        <div style={{ fontSize: 13, color: "#64748b" }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: accent, lineHeight: 1.1 }}>{value == null ? "—" : value}</div>
        {sub && <div style={{ fontSize: 12, color: "#94a3b8" }}>{sub}</div>}
      </div>
    </div>
  );
}

function rankHue(rank, total) { return total <= 1 ? 120 : Math.round(120 * (1 - rank / (total - 1))); }

function TableCard({ title, maxHeight = 380, children }) {
  return (
    <div className="card border-0 mb-3"><div className="card-body">
      <p className="chart-header mb-3" style={{ fontSize: 14, fontWeight: 600 }}>{title}</p>
      <div style={{ maxHeight, overflowY: "auto", overflowX: "auto" }}>{children}</div>
    </div></div>
  );
}

const th = { fontSize: 11, color: "#888", textTransform: "uppercase", textAlign: "left", fontWeight: 500, borderBottom: "1px solid #f1f1f1", padding: "6px 8px" };
const td = { padding: "6px 8px", borderBottom: "1px solid #f4f4f4", fontSize: 13 };

export default function SeoAnalytics() {
  const dispatch = useDispatch();
  const [totals, setTotals] = useState(null);
  const [brands, setBrands] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [t, b, q] = await Promise.all([
        get("/seo-analytics/totals"),
        get("/seo-analytics/brands"),
        get("/seo-analytics/questions"),
      ]);
      setTotals(t?.totals || null);
      setBrands(b?.brands || []);
      setQuestions(q?.questions || []);
    } catch (e) {
      setError(e?.message || "Failed to load SEO analytics");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);


  return (
    <div data-testid="seo-analytics-page" style={{ background: "#eaeaea" }}>
      {/* Header bar — same shape as Post Analytics / Dashboard. The global
          <Breadcrumbs/> (Home → SEO Analytics) renders just above this. */}
      <div className="inner-header-position">
        <div className="custom-container d-flex align-items-center justify-content-between flex-wrap">
          <div className="d-flex align-items-center">
            <span
              style={{ alignItems: "center", display: "flex", cursor: "pointer" }}
              onClick={() => dispatch(setSidebarStatus(true))}
            >
              <div className="cursor-pointer">
                <img src={Arrow} className="left-arrow-img" alt="" />
              </div>
              <p className="my-heading f-14 ml-3 mr-3">Side Menu</p>
            </span>
          </div>
          <div className="projActionButtons d-flex flex-wrap" style={{ maxWidth: "100vw" }}>
            <div className="btn-tool-group">
              <button
                disabled={loading}
                onClick={load}
                className="px-3 bg-white text-bid shadow-hover"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary mx-3">
        <div className="h-screen flex-grow-1 overflow-y-lg-auto">
          <main className="py-6 bg-surface-secondary">
            <div className="container-fluid">
              {/* Title row */}
              <div className="row mt-3">
                <div className="col-12">
                  <h2 style={{ margin: 0, fontSize: 22 }}>SEO Analytics</h2>
                  <div style={{ fontSize: 13, color: "#64748b" }}>
                    {brands.length} brand{brands.length === 1 ? "" : "s"} in scope
                  </div>
                </div>
              </div>

        {error && <div className="alert alert-warning mt-3" style={{ fontSize: 13 }}>{error}</div>}

        {/* KPI strip */}
        <div className="row">
          <div className="col-md col-6 mb-3"><StatCard label="Total views" value={(totals?.views ?? 0).toLocaleString()} accent="#0F62FE" /></div>
          <div className="col-md col-6 mb-3"><StatCard label="Real-user questions" value={totals?.realUserQuestions ?? 0} accent="#22c55e" sub={`of ${totals?.questions ?? 0} total`} /></div>
          <div className="col-md col-6 mb-3"><StatCard label="Upvotes" value={totals?.upvotes ?? 0} accent="#16a34a" /></div>
          <div className="col-md col-6 mb-3"><StatCard label="Downvotes" value={totals?.downvotes ?? 0} accent="#ef4444" /></div>
          <div className="col-md col-6 mb-3"><StatCard label="Published articles" value={totals?.publishedArticles ?? 0} accent="#a855f7" sub={`of ${totals?.articles ?? 0} total`} /></div>
          <div className="col-md col-6 mb-3"><StatCard label="Article likes" value={totals?.articleLikes ?? 0} accent="#ec4899" /></div>
          <div className="col-md col-6 mb-3"><StatCard label="Article shares" value={totals?.articleShares ?? 0} accent="#06b6d4" /></div>
        </div>

        {/* Per-brand ranking cards */}
        {brands.length > 0 && (
          <div className="card border-0 mb-3"><div className="card-body">
            <p className="chart-header mb-3" style={{ fontSize: 14, fontWeight: 600 }}>Brands by learning engagement (views)</p>
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
              {brands.map((b, i) => {
                const hue = rankHue(i, brands.length);
                return (
                  <div key={b.brandId || i} style={{ flex: "0 0 auto", minWidth: 190, borderRadius: 8, padding: "12px 14px", background: `hsl(${hue},75%,94%)`, border: `1px solid hsl(${hue},60%,72%)`, borderLeft: `4px solid hsl(${hue},65%,44%)` }}>
                    <div style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>#{i + 1}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{b.brandName || "(unknown)"}</div>
                    <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{b.views.toLocaleString()} views · {b.realUserQuestions} real Qs</div>
                    <div style={{ fontSize: 12, color: "#475569" }}>▲{b.upvotes} ▼{b.downvotes} · {b.publishedArticles} articles</div>
                    <div style={{ fontSize: 12, color: "#475569" }}>♥{b.articleLikes || 0} ⤴{b.articleShares || 0} likes/shares</div>
                  </div>
                );
              })}
            </div>
          </div></div>
        )}

        {/* Per-brand table */}
        <TableCard title="Per-brand breakdown">
          {loading && brands.length === 0 && <div style={{ color: "#999", padding: 16, textAlign: "center" }}>Loading…</div>}
          {!loading && brands.length === 0 && <div style={{ color: "#999", padding: 16, textAlign: "center" }}>No learning content yet.</div>}
          {brands.length > 0 && (
            <table style={{ width: "100%" }}>
              <thead><tr>{["Brand", "Questions", "Real-user", "Views", "Upvotes", "Downvotes", "Net", "Articles", "Likes", "Shares"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {brands.map((b) => (
                  <tr key={b.brandId}>
                    <td style={{ ...td, fontWeight: 600 }}>{b.brandName || "(unknown)"}</td>
                    <td style={td}>{b.questions}</td>
                    <td style={td}>{b.realUserQuestions}</td>
                    <td style={td}>{b.views.toLocaleString()}</td>
                    <td style={{ ...td, color: "#16a34a" }}>{b.upvotes}</td>
                    <td style={{ ...td, color: "#ef4444" }}>{b.downvotes}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{b.netVotes}</td>
                    <td style={td}>{b.publishedArticles}/{b.articles}</td>
                    <td style={{ ...td, color: "#ec4899" }}>{b.articleLikes || 0}</td>
                    <td style={{ ...td, color: "#06b6d4" }}>{b.articleShares || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableCard>

        {/* Top questions */}
        <TableCard title="Top questions by views">
          {questions.length === 0 && !loading && <div style={{ color: "#999", padding: 16, textAlign: "center" }}>No questions yet.</div>}
          {questions.length > 0 && (
            <table style={{ width: "100%" }}>
              <thead><tr>{["Question", "Brand", "Source", "Views", "▲", "▼", "Net"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {questions.map((q) => (
                  <tr key={q._id}>
                    <td style={{ ...td, maxWidth: 380 }}>{q.title}</td>
                    <td style={td}>{q.brandName || "—"}</td>
                    <td style={td}>{q.fromRealUser
                      ? <span style={{ fontSize: 11, background: "#dcfce7", color: "#166534", padding: "1px 6px", borderRadius: 4 }}>real user</span>
                      : <span style={{ fontSize: 11, background: "#e2e8f0", color: "#475569", padding: "1px 6px", borderRadius: 4 }}>auto FAQ</span>}</td>
                    <td style={td}>{q.views.toLocaleString()}</td>
                    <td style={{ ...td, color: "#16a34a" }}>{q.upvotes}</td>
                    <td style={{ ...td, color: "#ef4444" }}>{q.downvotes}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{q.netVotes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableCard>

              <p style={{ fontSize: 11, color: "#94a3b8", padding: "0 0 24px" }}>
                Views, votes, likes &amp; shares reflect engagement on the learning
                surface (app.prigmar.com/learning). Per-brand-site page visits
                aren&apos;t tracked yet.
              </p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
